/**
 * Centralized Error Logging System
 * 
 * Provides structured error logging for both client and server environments.
 * Logs are written to console in development and can be extended to send to
 * external services in production.
 */

export interface ErrorLogContext {
  component?: string;
  function?: string;
  props?: Record<string, any>;
  userId?: string;
  sessionId?: string;
  url?: string;
  userAgent?: string;
  timestamp?: string;
  [key: string]: any;
}

export interface ErrorLogEntry {
  id: string;
  timestamp: number;
  level: 'error' | 'warning' | 'info';
  message: string;
  error?: {
    name: string;
    message: string;
    stack?: string;
    code?: string;
  };
  context: ErrorLogContext;
  environment: 'client' | 'server';
  severity: 'low' | 'medium' | 'high' | 'critical';
}

class ErrorLogger {
  private logs: ErrorLogEntry[] = [];
  private maxLogs = 100; // Keep last 100 logs in memory
  private isDevelopment = process.env.NODE_ENV === 'development';

  /**
   * Log an error with context
   */
  logError(
    error: Error | unknown,
    context: ErrorLogContext = {},
    severity: ErrorLogEntry['severity'] = 'medium'
  ): string {
    const errorObj = this.normalizeError(error);
    const logEntry: ErrorLogEntry = {
      id: this.generateId(),
      timestamp: Date.now(),
      level: 'error',
      message: errorObj.message,
      error: {
        name: errorObj.name,
        message: errorObj.message,
        stack: errorObj.stack,
        code: (errorObj as any).code
      },
      context: {
        ...context,
        timestamp: new Date().toISOString(),
        ...(typeof window !== 'undefined' && {
          url: window.location.href,
          userAgent: navigator.userAgent
        })
      },
      environment: typeof window !== 'undefined' ? 'client' : 'server',
      severity
    };

    // Add to in-memory logs
    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift(); // Remove oldest log
    }

    // Console output in development
    if (this.isDevelopment) {
      console.group(`🔴 Error [${severity.toUpperCase()}]`);
      console.error('Message:', logEntry.message);
      console.error('Error:', errorObj);
      console.log('Context:', logEntry.context);
      console.log('Stack:', errorObj.stack);
      console.groupEnd();
    }

    // In production, you could send to external service:
    // this.sendToExternalService(logEntry);

    return logEntry.id;
  }

  /**
   * Log a warning
   */
  logWarning(
    message: string,
    context: ErrorLogContext = {},
    severity: ErrorLogEntry['severity'] = 'low'
  ): string {
    const logEntry: ErrorLogEntry = {
      id: this.generateId(),
      timestamp: Date.now(),
      level: 'warning',
      message,
      context: {
        ...context,
        timestamp: new Date().toISOString(),
        ...(typeof window !== 'undefined' && {
          url: window.location.href,
          userAgent: navigator.userAgent
        })
      },
      environment: typeof window !== 'undefined' ? 'client' : 'server',
      severity
    };

    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    if (this.isDevelopment) {
      console.warn(`⚠️ Warning [${severity.toUpperCase()}]:`, message, context);
    }

    return logEntry.id;
  }

  /**
   * Log informational message
   */
  logInfo(
    message: string,
    context: ErrorLogContext = {}
  ): string {
    const logEntry: ErrorLogEntry = {
      id: this.generateId(),
      timestamp: Date.now(),
      level: 'info',
      message,
      context: {
        ...context,
        timestamp: new Date().toISOString(),
        ...(typeof window !== 'undefined' && {
          url: window.location.href
        })
      },
      environment: typeof window !== 'undefined' ? 'client' : 'server',
      severity: 'low'
    };

    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    if (this.isDevelopment) {
      console.log(`ℹ️ Info:`, message, context);
    }

    return logEntry.id;
  }

  /**
   * Get all logs (for debugging)
   */
  getLogs(): ErrorLogEntry[] {
    return [...this.logs];
  }

  /**
   * Get logs filtered by criteria
   */
  getLogsByFilter(filter: {
    level?: ErrorLogEntry['level'];
    severity?: ErrorLogEntry['severity'];
    component?: string;
    since?: number; // timestamp
  }): ErrorLogEntry[] {
    return this.logs.filter(log => {
      if (filter.level && log.level !== filter.level) return false;
      if (filter.severity && log.severity !== filter.severity) return false;
      if (filter.component && log.context.component !== filter.component) return false;
      if (filter.since && log.timestamp < filter.since) return false;
      return true;
    });
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    this.logs = [];
  }

  /**
   * Export logs as JSON (for debugging/reporting)
   */
  exportLogs(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  /**
   * Download logs as file (client-side only)
   */
  downloadLogs(): void {
    if (typeof window === 'undefined') return;

    const logsJson = this.exportLogs();
    const blob = new Blob([logsJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-logs-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Normalize error to Error object
   */
  private normalizeError(error: Error | unknown): Error {
    if (error instanceof Error) {
      return error;
    }
    if (typeof error === 'string') {
      return new Error(error);
    }
    return new Error('Unknown error occurred');
  }

  /**
   * Generate unique ID for log entry
   */
  private generateId(): string {
    return `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Send to external service (e.g., Sentry, LogRocket, etc.)
   * Override this method to integrate with your error tracking service
   */
  private async sendToExternalService(logEntry: ErrorLogEntry): Promise<void> {
    // Example: Send to external service
    // if (process.env.NEXT_PUBLIC_ERROR_TRACKING_ENABLED === 'true') {
    //   try {
    //     await fetch('/api/log-error', {
    //       method: 'POST',
    //       headers: { 'Content-Type': 'application/json' },
    //       body: JSON.stringify(logEntry)
    //     });
    //   } catch (e) {
    //     console.error('Failed to send error log:', e);
    //   }
    // }
  }
}

// Singleton instance
export const errorLogger = typeof window !== 'undefined' 
  ? (window as any).__errorLogger || ((window as any).__errorLogger = new ErrorLogger())
  : new ErrorLogger();

// React hook for easy error logging in components
// Note: This hook must only be used in client components ('use client')
// Import useMemo at module level - this is safe as long as the hook is only called in client components
import { useMemo } from 'react';

export function useErrorLogger(componentName: string) {
  // Use useMemo to ensure stable function references across renders
  // This prevents infinite re-render loops when used in useEffect dependencies
  // Note: This hook should only be used in client components, so useMemo will always be available
  return useMemo(() => {
    return {
      logError: (error: Error | unknown, context: Omit<ErrorLogContext, 'component'> = {}, severity: ErrorLogEntry['severity'] = 'medium') => {
        return errorLogger.logError(error, { component: componentName, ...context }, severity);
      },
      logWarning: (message: string, context: Omit<ErrorLogContext, 'component'> = {}, severity: ErrorLogEntry['severity'] = 'low') => {
        return errorLogger.logWarning(message, { component: componentName, ...context }, severity);
      },
      logInfo: (message: string, context: Omit<ErrorLogContext, 'component'> = {}) => {
        return errorLogger.logInfo(message, { component: componentName, ...context });
      }
    };
  }, [componentName]);
}
